import {
  HttpContextToken,
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpHeaders,
  HttpInterceptor,
  HttpParams,
  HttpRequest
} from '@angular/common/http';
import { Injectable } from '@angular/core';
import { API_RUN_AT_BACKDOOR } from '@b3networks/api/file';
import {
  X as _X,
  CAPACITY_LIMIT_ERROR,
  CHAT_PUBLIC_PREFIX,
  DomainUtilsService,
  ERROR_PERMISSION,
  generateRandomString,
  isLocalhost,
  logger,
  UNEXPECTED_ERROR,
  X_B3_HEADER
} from '@b3networks/shared/common';
import { catchError, from, lastValueFrom, Observable, switchMap, tap, throwError, timeout, TimeoutError } from 'rxjs';
import { CustomEncoder } from './custom-encoder-param';

declare const X: any;

export interface FailFastConfig {
  timeout?: number; // in ms - timeout for the entire request
}
export const FAIL_FAST_CONFIG = new HttpContextToken<FailFastConfig>(() => ({}));

const PRIVATE_FILE_PREFIX = '/file/private';
const PUBLIC_FILE_PREFIX = '/file/public';

interface ErrorCustom {
  message: string;
  status?: number;
  error?: string;
  code?: string;
}

@Injectable({ providedIn: 'root' })
export class AuthCookieInterceptor implements HttpInterceptor {
  apiUrl = this.domainService.apiUrl;

  get X() {
    return typeof X !== 'undefined' ? X : _X;
  }

  constructor(protected domainService: DomainUtilsService) {}

  intercept(req: HttpRequest<unknown>, next: HttpHandler): Observable<HttpEvent<unknown>> {
    const config = req.context.get(FAIL_FAST_CONFIG);
    const startTime = Date.now();
    const corrId = generateRandomString(10);
    const builtReq = this._buildRequest(req, { corrId, startTime });

    // Execute request with or without XSRF token
    const result = this._executeRequest(req, next, builtReq);

    // Apply timeout if fail-fast
    const resultWithTimeout = config?.timeout && config.timeout > 0 ? result.pipe(timeout(config.timeout)) : result;
    return resultWithTimeout.pipe(
      tap({
        complete: () => this._logRequestComplete(req, corrId, startTime),
        error: (err: any) => this._logRequestError(req, corrId, startTime, err)
      }),
      catchError((err: any) => this._handleRequestError(req, next, err))
    );
  }

  private _executeRequest(
    req: HttpRequest<unknown>,
    next: HttpHandler,
    builtReq: HttpRequest<unknown>
  ): Observable<HttpEvent<unknown>> {
    if (
      (req.method === 'POST' || req.method === 'PUT') &&
      req.url != 'auth/private/v1/xsrf' &&
      req.headers.get(X_B3_HEADER.validateXSRFToken) === 'true'
    ) {
      const logMessage = `${new Date()}: issue xsrf token`;
      console.log(logMessage + '...');

      return from(this._generateXSRFToken(req, next)).pipe(
        switchMap(() => {
          console.log(`${new Date()}: continue to call api ${builtReq.url}`);
          return next.handle(builtReq);
        })
      );
    } else {
      return next.handle(builtReq);
    }
  }

  private _logRequestComplete(req: HttpRequest<unknown>, corrId: string, startTime: number): void {
    const endTime = Date.now();
    const duration = endTime - startTime;
    if ((!req.url.includes('chat_public/timestamp') && !isLocalhost()) || duration > 500) {
      // logger.info(`Request to ${req.method}:${req.url} with correlationId ${corrId} took ${duration}ms`);
    }
  }

  private _logRequestError(req: HttpRequest<unknown>, corrId: string, startTime: number, err: any): void {
    const endTime = Date.now();
    const duration = endTime - startTime;

    if (err instanceof TimeoutError) {
      logger.warn(
        `Fail-fast request to ${req.method}:${req.url} with correlationId ${corrId} timed out after ${duration}ms`
      );
    }

    if (err instanceof HttpErrorResponse) {
      logger.warn(
        `Request to ${req.method}:${req.url} with correlationId ${corrId} failed after ${duration}ms with statusCode ${err.status} and error ${err.error?.message}`
      );
    }
  }

  private _handleRequestError(req: HttpRequest<unknown>, next: HttpHandler, err: any): Observable<never> {
    // Handle timeout errors specifically for fail-fast requests
    if (err instanceof TimeoutError) {
      const timeoutError: ErrorCustom = {
        message: `Request timed out `,
        status: 408, // Request Timeout
        error: 'TIMEOUT_ERROR',
        code: 'TIMEOUT'
      };
      return throwError(() => timeoutError);
    }

    // Handle HTTP errors
    if (err instanceof HttpErrorResponse) {
      return this._handleHttpError(req, next, err);
    }

    // Handle other errors
    return throwError(() => err);
  }

  private _handleHttpError(req: HttpRequest<unknown>, next: HttpHandler, err: HttpErrorResponse): Observable<never> {
    from(this._handleError(req, next, err));
    const errCustom = this._transformErrorFormat(err);
    const isChatPublic = this._isChatRequest(req.url);

    // expired session on private api
    if (errCustom.status === 401 && !isChatPublic) {
      from(this._handle401Error(req, next, err));
    }
    // blocked ipaddress of admin portal
    if (errCustom.status === 400 && errCustom?.['sec'] === 203) {
      from(this._handle401Error(req, next, err));
    }

    return throwError(() => errCustom);
  }

  protected _transformErrorFormat(err: HttpErrorResponse) {
    let errCustom: ErrorCustom = err?.error ? err?.error : err;

    if (typeof errCustom === 'string') {
      errCustom = Object.assign({}, <ErrorCustom>{
        message: UNEXPECTED_ERROR.message,
        error: err.error,
        code: UNEXPECTED_ERROR.code,
        status: err.status
      });
    } else {
      errCustom = Object.assign(errCustom, <ErrorCustom>{
        status: err.status
      });
    }

    const e = err ? err?.error : err;
    if (err.status === 401 && !e?.message) {
      errCustom = Object.assign(errCustom, <ErrorCustom>{
        message: ERROR_PERMISSION.message,
        code: ERROR_PERMISSION.code,
        status: err.status
      });
    } else if (!e?.message) {
      errCustom = Object.assign(errCustom, <ErrorCustom>{
        message: UNEXPECTED_ERROR.message,
        code: UNEXPECTED_ERROR.code,
        status: err.status
      });
    }

    // Jira: B3-12800 - Update Error Message for Capacity Limit
    if (errCustom?.code === CAPACITY_LIMIT_ERROR.code) {
      errCustom = Object.assign(errCustom, {
        message: CAPACITY_LIMIT_ERROR.message
      });
    }
    return errCustom;
  }

  protected _buildRequest(req: HttpRequest<unknown>, addon?: { corrId?: string; startTime?: number }) {
    return req.clone({
      url: this.buildUrl(req.url),
      headers: this.buildHeaders(req.headers, addon),
      withCredentials: false,
      params: req.params ? this.buildParams(req.params) : req.params
    });
  }

  protected async _handleError(request: HttpRequest<unknown>, next: HttpHandler, err: HttpErrorResponse) {
    let e = err ? err?.error : err;
    if (err?.error instanceof Blob) {
      const er = await (err.error as Blob).text();
      e = this.parseJsonString(er) || e;
    }
    return throwError(() => e);
  }

  protected async _handle401Error(request: HttpRequest<unknown>, next: HttpHandler, err: HttpErrorResponse) {
    this.X.showAuth();

    let e = err ? err?.error : err;
    if (err?.error instanceof Blob) {
      const er = await (err.error as Blob).text();
      e = this.parseJsonString(er) || e;
    }

    return throwError(() => e);
  }

  protected buildUrl(url: string) {
    if (this.validUrl(url) || url.startsWith('assets')) {
      return url;
    }

    if (url.indexOf('/') !== 0) {
      url = '/' + url;
    }

    const hasIgnoreZuul = API_RUN_AT_BACKDOOR.some(api => url.startsWith(api));
    if (hasIgnoreZuul) {
      if (url.startsWith(PRIVATE_FILE_PREFIX)) {
        url = url.slice(PRIVATE_FILE_PREFIX.length);
      } else if (url.startsWith(PUBLIC_FILE_PREFIX)) {
        url = url.slice(PUBLIC_FILE_PREFIX.length);
      }

      return this.domainService.storageBackdoorAPIUrl + (url ? url : '');
    }

    return this.apiUrl + (url ? url : '');
  }

  /**
   * Temporary fix for Angular Team
   * https://github.com/angular/angular/issues/18261
   * @param params
   */
  private buildParams(params: HttpParams) {
    let overridedParams = new HttpParams({ encoder: new CustomEncoder() });
    params.keys().forEach(key => {
      overridedParams = overridedParams.set(key, params.get(key));
    });
    return overridedParams;
  }

  protected _isChatRequest(url: string) {
    return (
      url.includes(CHAT_PUBLIC_PREFIX) ||
      url.includes('public/_tc') ||
      url.includes('public/v2/_tc') ||
      url.includes('public/v2/user') ||
      url.includes('public/v3/namespace')
    );
  }

  private validUrl(url: string) {
    return /(ftp|http|https):\/\/(\w+:{0,1}\w*@)?(\S+)(:[0-9]+)?(\/|\/([\w#!:.?+=&%@!\-\/]))?/.test(url);
  }

  protected buildHeaders(headers: HttpHeaders, addonData?: { corrId?: string; startTime?: number }) {
    headers = headers || new HttpHeaders();

    const orgUuid = this.X.orgUuid;
    const session = this.X.sessionToken;
    //sometime we set org info on api caller
    if (!headers.has(X_B3_HEADER.orgUuid) && !!orgUuid) {
      headers = headers.set(X_B3_HEADER.orgUuid, orgUuid);
    }

    if (!headers.has(X_B3_HEADER.sessionToken) && session) {
      headers = headers.set(X_B3_HEADER.sessionToken, session);
    }

    if (!headers.has(X_B3_HEADER.correlationId)) {
      const corrId = addonData?.corrId || generateRandomString(10);
      headers = headers.set(X_B3_HEADER.correlationId, corrId);
    }

    if (!headers.has(X_B3_HEADER.clientTime)) {
      const startAt = addonData?.startTime || Date.now();
      headers = headers.set(X_B3_HEADER.clientTime, startAt.toString()); // for calculating duration
    }

    return headers;
  }

  private async _generateXSRFToken(request: HttpRequest<unknown>, next: HttpHandler) {
    return await lastValueFrom(this._generateXSRFTokenAsync(request, next));
  }

  private _generateXSRFTokenAsync(request: HttpRequest<unknown>, next: HttpHandler) {
    let uri = request.method + ':';
    uri += request.url.startsWith('/') ? request.url : '/' + request.url;
    const generateXSRFTokenReq = new HttpRequest(
      'POST',
      this.buildUrl('auth/private/v1/xsrf'),
      { uri: uri },
      { headers: this.buildHeaders(new HttpHeaders()), withCredentials: isLocalhost() ? true : false }
    );

    return next.handle(generateXSRFTokenReq).pipe(
      catchError(() => {
        return null;
      })
    );
  }

  private parseJsonString(str: string) {
    try {
      return JSON.parse(str);
    } catch (e) {
      return false;
    }
  }
}
